import { PlusOutlined } from "@ant-design/icons";
import { Button, notification } from "antd";
import moment from "moment-timezone";
import React, { useMemo, useState } from "react";
import styled from "styled-components";

import { EmailDocument } from "../../../api/graphql";
import {
  ExternalTransactionInitialValues,
  ExternalTransactionPopup,
} from "../VerificationView/TransactionList/ExternalTransactionPopup";
import { getAsset } from "./EmailDocumentPreview";
import { SAVE_DATE_FORMAT } from "@/v1/constants";

const Wrapper = styled.div`
  padding: 24px;
  display: flex;
  border: 1px solid #f0f0f0;
  justify-content: space-between;
  align-items: center;

  > div {
    flex-direction: column;
  }
`;

const Title = styled.p`
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  rgba(0, 0, 0, 0.85);
  margin-bottom: 4px;
`;

const Subtitle = styled.p`
  color: rgba(0, 0, 0, 0.45);
  padding-right: 20px;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  margin: 0;
`;

const getPrefilledData = (
  emailDocument: EmailDocument
): ExternalTransactionInitialValues => ({
  amount: emailDocument.amount,
  name: emailDocument.name,
  description: emailDocument.documentNumber,
  iban: emailDocument.iban,
  valutaDate: moment(emailDocument.date).isValid()
    ? emailDocument.date
    : moment().format(SAVE_DATE_FORMAT),
  assets: [getAsset(emailDocument)],
});

const NoMatchSection = ({
  email,
  emailDocument,
  onCreateExternalTransaction,
}: {
  email: string;
  emailDocument: EmailDocument;
  onCreateExternalTransaction: () => void;
}) => {
  const [showExternalTransactionPopup, setShowExternalTransactionPopup] =
    useState<boolean>(false);

  // Stable across re-renders while the popup is open: getPrefilledData
  // builds a fresh object every call, and ExternalTransactionPopup's
  // `useEffect(..., [initialValues, form])` re-applies it to the form
  // whenever that identity changes - a new reference on every
  // NoMatchSection render would overwrite whatever the operator already
  // typed.
  const initialValues = useMemo(
    () => getPrefilledData(emailDocument),
    [emailDocument]
  );

  return (
    <>
      <Wrapper>
        <div>
          <Title>Can’t find a match?</Title>
          <Subtitle>
            There is no matching transaction for this receipt? You can create an
            external transaction for it.
          </Subtitle>
        </div>
        <Button
          icon={<PlusOutlined />}
          onClick={() => setShowExternalTransactionPopup(true)}
        >
          Create
        </Button>
      </Wrapper>
      <ExternalTransactionPopup
        hideAssetActions
        // Same reason as in TransactionListContainer: the receipt on screen is
        // the reliable source of the customer, the `email` URL parameter lags
        // one receipt behind (SMT-1320).
        email={emailDocument.userEmail ?? email}
        isPopupShown={showExternalTransactionPopup}
        onSuccess={(
          _: unknown,
          isDuplicate: boolean,
          wasDuplicatedTransaction: boolean
        ) => {
          if (!isDuplicate) {
            setTimeout(onCreateExternalTransaction, 2000);
            // A duplicated transaction isn't linked to the email document
            // (it upserts with empty options), so it must not claim to be.
            notification.success({
              message: wasDuplicatedTransaction
                ? "Created external transaction"
                : "Created external transaction with linked email document",
            });
          } else {
            notification.success({
              message: "Created external transaction",
            });
          }
        }}
        closePopup={() => {
          setShowExternalTransactionPopup(false);
        }}
        initialValues={initialValues}
        emailDocumentId={emailDocument.id}
      />
    </>
  );
};

export default NoMatchSection;
